import { rateGuarantee } from "./sharedTypedAmount";

import type {
  TypedAmountSpec,
  TypedAmountTypeOption,
} from "../typedAmountTypes";

const AMOUNT_LABEL = "Amount";
const DOLLAR_AMOUNT_VALUE = "dollarAmount";
const DOLLAR_AMOUNT_LABEL = "Dollar amount";
const NOT_INCLUDED_LABEL = "Not Included";

/**
 * Cancer typed-amount field registry. Maps the v5 field id to its
 * `{type, amount}` companion field names and the type-select options the
 * popover editor offers.
 *
 * Sentinel options (`hideAmount: true`) collapse to a single label
 * (e.g. "Not Included") with no amount input.
 *
 * Only `dollarAmount` / `scheduleUpToDollarAmount` style fields are
 * listed here. Enum-only fields (`portability`, `waiverOfPremium`, etc.)
 * use the cellFormatOverride text path and a separate enum editor (TBD).
 */

const SENTINEL_OPTIONS: TypedAmountTypeOption[] = [
  { value: "notDisclosed", label: "Not Disclosed", hideAmount: true },
  { value: "notIncluded", label: NOT_INCLUDED_LABEL, hideAmount: true },
  { value: "notOffered", label: "Not Offered", hideAmount: true },
];

const DOLLAR_AMOUNT_OPTIONS: TypedAmountTypeOption[] = [
  { value: DOLLAR_AMOUNT_VALUE, label: DOLLAR_AMOUNT_LABEL },
  ...SENTINEL_OPTIONS,
];

const WELLNESS_OPTIONS: TypedAmountTypeOption[] = [
  { value: DOLLAR_AMOUNT_VALUE, label: DOLLAR_AMOUNT_LABEL },
  { value: "notDisclosed", label: "Not Disclosed", hideAmount: true },
  { value: "notIncluded", label: NOT_INCLUDED_LABEL, hideAmount: true },
  { value: "notOffered", label: "Not Offered", hideAmount: true },
];

const SCHEDULE_UP_TO_OPTIONS: TypedAmountTypeOption[] = [
  { value: "scheduleUpToDollarAmount", label: "Schedule up to" },
  ...SENTINEL_OPTIONS,
];

export const cancerTypedAmountFields: Record<string, TypedAmountSpec> = {
  hospice: {
    typeKey: "hospiceType",
    amountKey: "hospiceAmount",
    amountLabel: AMOUNT_LABEL,
    amountMin: 1,
    amountInteger: true,
    typeOptions: [
      { value: DOLLAR_AMOUNT_VALUE, label: DOLLAR_AMOUNT_LABEL },
      {
        value: "dollarAmountPerDayPerLifetime",
        label: "Dollar amount per day per lifetime",
      },
      ...SENTINEL_OPTIONS,
    ],
    extraKeys: [
      {
        key: "hospiceDuration",
        label: "Duration (days)",
        showForTypes: ["dollarAmountPerDayPerLifetime"],
        defaultValue: 1,
      },
    ],
  },
  diagnosisBenefit: {
    typeKey: "diagnosisBenefitType",
    amountKey: "diagnosisBenefitAmount",
    amountLabel: AMOUNT_LABEL,
    typeOptions: DOLLAR_AMOUNT_OPTIONS,
    amountMin: 1,
    amountInteger: true,
  },
  radiationTherapyChemo: {
    typeKey: "radiationTherapyChemoType",
    amountKey: "radiationTherapyChemoAmount",
    amountLabel: AMOUNT_LABEL,
    typeOptions: DOLLAR_AMOUNT_OPTIONS,
    amountMin: 1,
    amountInteger: true,
  },
  bloodPlasmaPlatelets: {
    typeKey: "bloodPlasmaPlateletsType",
    amountKey: "bloodPlasmaPlateletsAmount",
    amountLabel: AMOUNT_LABEL,
    typeOptions: DOLLAR_AMOUNT_OPTIONS,
    amountMin: 1,
    amountInteger: true,
  },
  hospitalConfinementInitial: {
    typeKey: "hospitalConfinementInitialType",
    amountKey: "hospitalConfinementInitialAmount",
    amountLabel: AMOUNT_LABEL,
    typeOptions: DOLLAR_AMOUNT_OPTIONS,
    amountMin: 1,
    amountInteger: true,
  },
  icuConfinementInitial: {
    typeKey: "icuConfinementInitialType",
    amountKey: "icuConfinementInitialAmount",
    amountLabel: AMOUNT_LABEL,
    typeOptions: DOLLAR_AMOUNT_OPTIONS,
    amountMin: 1,
    amountInteger: true,
  },
  wellnessBenefit: {
    typeKey: "wellnessBenefitType",
    amountKey: "wellnessBenefitAmount",
    amountLabel: AMOUNT_LABEL,
    typeOptions: WELLNESS_OPTIONS,
    amountMin: 1,
    amountInteger: true,
  },
  skinCancer: {
    typeKey: "skinCancerType",
    amountKey: "skinCancerAmount",
    amountLabel: AMOUNT_LABEL,
    typeOptions: SCHEDULE_UP_TO_OPTIONS,
    amountMin: 1,
    amountInteger: true,
  },
  surgicalBenefit: {
    typeKey: "surgicalBenefitType",
    amountKey: "surgicalBenefitAmount",
    amountLabel: AMOUNT_LABEL,
    typeOptions: SCHEDULE_UP_TO_OPTIONS,
    amountMin: 1,
    amountInteger: true,
  },
  rateGuarantee,
};
